type Getter<V> = () => V;
type Setter<V> = (value: V) => void;

type Effect = (() => void) | (() => () => void);

type EffectOwner = {
  $deps: Set<Set<EffectOwner>>;
  $update: () => void;
  $dispose: () => void;
};

type ScopeOwner = {
  $effects: Set<EffectOwner>;
  $dispose: () => void;
};

type TransactionOwner = {
  $effects: Set<EffectOwner>;
};

type Owner = {
  $scope: ScopeOwner;
  $effect: EffectOwner | null;
  $transaction: TransactionOwner | null;
};

const $root = createScopeOwner();

const $current: Owner = {
  $scope: $root,
  $effect: null,
  $transaction: null,
};

function createEffectOwner(
  update: () => void,
  dispose = () => {}
): EffectOwner {
  return {
    $deps: new Set(),
    $update: update,
    $dispose: dispose,
  };
}

function createScopeOwner(): ScopeOwner {
  const effects = new Set<EffectOwner>();

  return {
    $effects: effects,
    $dispose: () => {
      Array.from(effects)
        .reverse()
        .forEach((effect) => effect.$dispose());
    },
  };
}

export function createSignal<V>(
  initialValue: V
): readonly [get: Getter<V>, set: Setter<V>] {
  const effects = new Set<EffectOwner>();

  let value = initialValue;

  const getter = () => {
    if ($current.$effect) {
      effects.add($current.$effect);
      $current.$effect.$deps.add(effects);
    }
    return value;
  };

  const setter = (newValue: V) => {
    if (value !== newValue) {
      value = newValue;
      effects.forEach((effect) => {
        if ($current.$transaction) {
          $current.$transaction.$effects.add(effect);
        } else {
          effect.$update();
        }
      });
    }
  }

  return [getter, setter] as const;
}

export function createEffect(fn: Effect): () => void {
  let cleanup: ReturnType<Effect> | null = null;

  const effect = createEffectOwner(
    () => {
      $current.$effect = effect;
      cleanup?.();
      cleanup = fn();
      $current.$effect = null;
    },
    () => {
      cleanup?.();
      cleanup = null;
      effect.$deps.forEach((effects) => effects.delete(effect));
      effect.$deps.clear();
    }
  );

  $current.$scope.$effects.add(effect);
  effect.$update();
  return effect.$dispose;
}

export function createTransaction(fn: () => void): void {
  if ($current.$transaction) {
    return fn();
  }

  const transaction = { $effects: new Set<EffectOwner>() };
  $current.$transaction = transaction;
  fn();
  transaction.$effects.forEach((effect) => effect.$update());
  $current.$transaction = null;
}

export function createScope<T>(fn: (dispose: () => void) => T): T {
  const currentScope = createScopeOwner();
  const prevScope = $current.$scope;
  $current.$scope = currentScope;
  const data = fn(currentScope.$dispose);
  $current.$scope = prevScope;
  return data;
}

export function createRef<T>() {
  let value: T;
  return (newValue?: T) => {
    if (newValue !== undefined) {
      value = newValue;
    }

    return value;
  };
}

export function untrack<T>(fn: () => T): T {
  const prevEffect = $current.$effect;
  $current.$effect = null;
  const result = fn();
  $current.$effect = prevEffect;
  return result;
};
